import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Printer } from 'lucide-react';
import { db } from "../firebase/config";
import { collection, getDocs, query, where } from "firebase/firestore";
import { toast } from '@/hooks/use-toast';
import { Pencil, Save, Loader2, Edit, Check, Sparkles, FileText, User } from "lucide-react";
import { generateAutoRemarks } from '@/services/aiService';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { doc, updateDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { ReportPreview } from "@/components/Headerfiles/ReportPreview";
import { StudentData } from "@/components/Headerfiles/types";

interface StudentResultDetailsProps {
  student: {
    id: string;
    name: string;
    regNumber: string;
    photoUrl?: string;
  };
  session: {
    id: string;
    name: string;
  };
  schoolID: string;
  onBack: () => void;
  position?: number; // Class Position
  totalStudents?: number; // Class Total
  overallPosition?: number; // Stream Position (Grade-wide)
  overallTotal?: number; // Grade Total
  allowEditing?: boolean;
  showAiActions?: boolean;
  canEditPrincipalRemarks?: boolean;
}

interface GradeDetail {
  id: string;
  subjectId: string;
  subjectName: string;
  examTypeId: string;
  examTypeName: string; // e.g., "Mid Term"
  categoryName: string; // e.g., "Paper 1"
  categoryId?: string;
  marks: number;
  maxMarks: number;
  categoryWeight?: number; // Weight percentage of this category
  normalizedScore: number;
  grade: string;
  remarks: string;
}

interface ExamType {
  id: string;
  name: string;
  weight: number;
}

interface SubjectSummary {
  subjectId: string;
  subjectName: string;
  totalScore: number;
  grade: string;
  details: GradeDetail[];
}

interface GradeBoundary {
  grade: string;
  min: number;
  max: number;
}

const StudentResultDetails = ({ 
  student, 
  session, 
  schoolID, 
  onBack, 
  position, 
  totalStudents, 
  overallPosition, 
  overallTotal,
  allowEditing = true,
  showAiActions = true,
  canEditPrincipalRemarks = false
}: StudentResultDetailsProps) => {
  const [loading, setLoading] = useState(true);
  const [subjectSummaries, setSubjectSummaries] = useState<SubjectSummary[]>([]);
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [schoolName, setSchoolName] = useState("");
  const [studentClass, setStudentClass] = useState("");

  // New State for Remarks
  const [principalRemarks, setPrincipalRemarks] = useState("");
  const [academicAnalysis, setAcademicAnalysis] = useState("");


  useEffect(() => {
    fetchStudentDetails();
    fetchRemarks();
  }, [student.id, session.id, schoolID]);

  const fetchRemarks = async () => {
    try {
      const remarksRef = doc(db, `schools/${schoolID}/studentSessionRemarks/${session.id}_${student.id}`);
      const remarksSnap = await getDoc(remarksRef);
      
      if (remarksSnap.exists()) {
        const data = remarksSnap.data();
        setPrincipalRemarks(data.principalRemarks || "");
        setAcademicAnalysis(data.academicAnalysis || "");
      } else {
        setPrincipalRemarks("");
        setAcademicAnalysis("");
      }
    } catch (error) {
      console.error("Error fetching remarks:", error);
    }
  };



  const fetchStudentDetails = async () => {
    setLoading(true);
    try {
      // Fetch school details for report
      const schoolDetailsDoc = await getDoc(doc(db, `schools/${schoolID}/schoolDetails/main`));
      if (schoolDetailsDoc.exists()) {
        setSchoolName(schoolDetailsDoc.data().name || "");
      }

      // Fetch student's class level
      const studentDoc = await getDoc(doc(db, `schools/${schoolID}/students/${student.id}`));
      if (studentDoc.exists()) {
        const studentData = studentDoc.data();
        // Try different field names for class (className is the primary field used in registration)
        const classValue = studentData.className || studentData.class || studentData.form || studentData.grade || studentData.gradeLevel || "";
        console.log("Student class data:", { className: studentData.className, class: studentData.class, form: studentData.form, resolved: classValue });
        setStudentClass(classValue);
      }

      // Fetch active grading scheme for boundaries
      const schemesCollection = collection(db, `schools/${schoolID}/gradingSchemes`);
      const schemesSnapshot = await getDocs(schemesCollection);
      const activeScheme = schemesSnapshot.docs.find(doc => doc.data().isActive);
      let boundaries: GradeBoundary[] = [];
      if (activeScheme) {
        boundaries = activeScheme.data().boundaries || [];
      }

      // Fetch subjects to map names
      const subjectsCollection = collection(db, `schools/${schoolID}/subjects`);
      const subjectsSnapshot = await getDocs(subjectsCollection);
      const subjectsMap = new Map(subjectsSnapshot.docs.map(doc => [doc.id, doc.data().name]));

      // Fetch exam types to map names
      const sessionDoc = await getDocs(query(collection(db, `schools/${schoolID}/examSessions`), where('__name__', '==', session.id)));
      const sessionData = sessionDoc.docs[0]?.data();
      const sessionExamTypes: ExamType[] = sessionData?.examTypes || [];
      console.log("Exam types fetched from Firebase:", sessionExamTypes);
      setExamTypes(sessionExamTypes);
      
      const examTypesMap = new Map<string, string>(sessionExamTypes.map((et) => [et.id, et.name]));

      // Build valid category set for this session. If categories exist for a subject+examType,
      // we only display grades tied to those categoryIds. This prevents old categories from "crashing" into new ones.
      const categoriesCollection = collection(db, `schools/${schoolID}/examCategories`);
      const sessionCategoriesQuery = query(categoriesCollection, where('examSessionId', '==', session.id));
      const sessionCategoriesSnapshot = await getDocs(sessionCategoriesQuery);
      const validCategoryIdsBySubjectExamType = new Map<string, Set<string>>();
      const categoriesMap = new Map<string, { maxMarks: number; weight: number; name: string }>();
      sessionCategoriesSnapshot.docs.forEach((docSnap) => {
        const data: any = docSnap.data();
        const subjectId = data.subjectId;
        const examTypeId = data.examTypeId;
        if (!subjectId || !examTypeId) return;
        const key = `${subjectId}_${examTypeId}`;
        if (!validCategoryIdsBySubjectExamType.has(key)) {
          validCategoryIdsBySubjectExamType.set(key, new Set());
        }
        validCategoryIdsBySubjectExamType.get(key)!.add(docSnap.id);
        
        // Store category details for later use
        categoriesMap.set(docSnap.id, {
          maxMarks: data.maxMarks || 100,
          weight: data.weight || 0,
          name: data.name || ''
        });
      });

      // Fetch grades
      const gradesCollection = collection(db, `schools/${schoolID}/grades`);
      const q = query(
        gradesCollection,
        where("studentId", "==", student.id),
        where("examSessionId", "==", session.id)
      );
      const gradesSnapshot = await getDocs(q);

      const summariesMap = new Map<string, SubjectSummary>();

      // Deduplicate: for each subject+examType+category keep the best doc.
      // Prefer submitted (isDraft=false) over draft; then prefer newest timestamp.
      const bestByEntryKey = new Map<string, { id: string; data: any }>();
      const getMillis = (ts: any): number => {
        try {
          return typeof ts?.toMillis === 'function' ? ts.toMillis() : 0;
        } catch {
          return 0;
        }
      };
      const getBestTimestamp = (data: any): number => {
        return (
          getMillis(data.submittedAt) ||
          getMillis(data.updatedAt) ||
          getMillis(data.createdAt) ||
          0
        );
      };
      const isSubmitted = (data: any): boolean => data.isDraft !== true;

      gradesSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        // If a category set exists for subject+examType in this session, only accept grades tied to those categoryIds.
        const subjectId = data.subjectId;
        const examTypeId = data.examTypeId;
        const key = `${subjectId}_${examTypeId}`;
        const validSet = validCategoryIdsBySubjectExamType.get(key);
        if (validSet && validSet.size > 0) {
          if (!data.categoryId || !validSet.has(data.categoryId)) {
            return;
          }
        }

        const categoryKey = data.categoryId || (data.categoryName || '').toString().trim().toLowerCase() || 'default';
        const entryKey = `${data.subjectId}_${data.examTypeId}_${categoryKey}`;

        const existing = bestByEntryKey.get(entryKey);
        if (!existing) {
          bestByEntryKey.set(entryKey, { id: docSnap.id, data });
          return;
        }

        const a = existing.data;
        const b = data;

        // Prefer submitted over draft
        if (isSubmitted(b) && !isSubmitted(a)) {
          bestByEntryKey.set(entryKey, { id: docSnap.id, data: b });
          return;
        }
        if (isSubmitted(b) === isSubmitted(a)) {
          // Prefer newer
          if (getBestTimestamp(b) > getBestTimestamp(a)) {
            bestByEntryKey.set(entryKey, { id: docSnap.id, data: b });
          }
        }
      });

      // Build summaries from de-duplicated grades
      Array.from(bestByEntryKey.values()).forEach(({ id, data }) => {
        const subjectName = subjectsMap.get(data.subjectId) || 'Unknown Subject';
        const examTypeName = examTypesMap.get(data.examTypeId) || 'Unknown Exam';

        if (!summariesMap.has(data.subjectId)) {
          summariesMap.set(data.subjectId, {
            subjectId: data.subjectId,
            subjectName: subjectName,
            totalScore: 0,
            grade: 'N/A',
            details: []
          });
        }

        const summary = summariesMap.get(data.subjectId)!;
        
        // Add to details with category info
        const categoryInfo = data.categoryId ? categoriesMap.get(data.categoryId) : null;
        summary.details.push({
          id,
          subjectId: data.subjectId,
          subjectName: subjectName,
          examTypeId: data.examTypeId,
          examTypeName: examTypeName,
          categoryName: data.categoryName || categoryInfo?.name || 'Main',
          categoryId: data.categoryId,
          marks: data.marks,
          maxMarks: categoryInfo?.maxMarks || 100,
          categoryWeight: categoryInfo?.weight,
          normalizedScore: data.normalizedExamScore || 0,
          grade: data.grade,
          remarks: data.remarks
        });

        // Accumulate total score
        summary.totalScore += (data.normalizedExamScore || 0);
      });

      // Calculate final grades
      const summaries = Array.from(summariesMap.values()).map(summary => {
        summary.grade = calculateGrade(summary.totalScore, boundaries);
        return summary;
      });

      setSubjectSummaries(summaries);

    } catch (error) {
      console.error("Error fetching student details:", error);
      toast({
        title: "Error",
        description: "Failed to load student details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateGrade = (marks: number, boundaries: GradeBoundary[]): string => {
    if (!boundaries.length) return 'N/A';
    const roundedMarks = Math.round(marks);
    const sortedBoundaries = [...boundaries].sort((a, b) => b.min - a.min);
    for (const boundary of sortedBoundaries) {
      if (roundedMarks >= boundary.min && roundedMarks <= boundary.max) {
        return boundary.grade;
      }
    }
    return 'N/A';
  };

  const getGradeColor = (grade: string) => {
    const gradeColors: { [key: string]: string } = {
      'A': 'bg-green-500',
      'B': 'bg-blue-500',
      'C': 'bg-yellow-500',
      'D': 'bg-orange-500',
      'E': 'bg-red-500',
    };
    return gradeColors[grade] || 'bg-gray-500';
  };

  // Edit Grade State
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [editingGrade, setEditingGrade] = useState<GradeDetail | null>(null);
  const [editMarks, setEditMarks] = useState<string>('');

  // Remarks State: Map<subjectId, remark>
  const [remarksState, setRemarksState] = useState<Record<string, string>>({});

  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  // AI Auto Remarks State
  const [isAutoRemarksEnabled, setIsAutoRemarksEnabled] = useState(false);
  const [isGeneratingRemarks, setIsGeneratingRemarks] = useState(false);

  // Initialize remarks state when summaries change
  useEffect(() => {
    const initialRemarks: Record<string, string> = {};
    subjectSummaries.forEach(s => {
       const remark = s.details.find(d => d.remarks)?.remarks || '';
       initialRemarks[s.subjectId] = remark;
    });
    setRemarksState(initialRemarks);
  }, [subjectSummaries]);

  const handleValidation = async () => {
      // Toggle Mode
      if (isEditingMode) {
          // Saving Changes
          setIsSaving(true);
          try {
             const updatePromises = subjectSummaries.flatMap(summary => {
                const newRemark = remarksState[summary.subjectId];
                // Only update if changed (optional optimization, but good practice)
                // for simplicity we update all associated grade entries for the subject
                return summary.details.map(detail => {
                   const gradeRef = doc(db, `schools/${schoolID}/grades`, detail.id);
                   return updateDoc(gradeRef, {
                     remarks: newRemark || '', // Ensure empty string if undefined
                     updatedAt: serverTimestamp()
                   });
                }).concat([]);
             });
             
             await Promise.all(updatePromises);

             // NEW: Save Principal Remarks if allowed
             if (canEditPrincipalRemarks) {
                 const remarksRef = doc(db, `schools/${schoolID}/studentSessionRemarks/${session.id}_${student.id}`);
                 await import('firebase/firestore').then(({ setDoc }) => 
                   setDoc(remarksRef, {
                     principalRemarks,
                     academicAnalysis,
                     studentId: student.id,
                     sessionId: session.id,
                     updatedAt: serverTimestamp(),
                     studentName: student.name,
                     regNumber: student.regNumber
                   }, { merge: true })
                 );
             }
             
             toast({
                title: "Changes Saved",
                description: "Remarks and grades have been saved successfully.",
             });
             
             fetchStudentDetails(); // Refresh
             setIsEditingMode(false);

          } catch (error) {
              console.error("Error saving remarks:", error);
              toast({
                  title: "Save Failed",
                  description: "Could not save remarks.",
                  variant: "destructive"
              });
          } finally {
              setIsSaving(false);
          }
      } else {
          // Enter Edit Mode
          setIsEditingMode(true);
      }
  };


  const handleAutoRemarksToggle = async (checked: boolean) => {
    setIsAutoRemarksEnabled(checked);
    
    // Ensure we are in edit mode if enabling AI
    if (checked && !isEditingMode) {
        setIsEditingMode(true);
    }

    if (checked) {
        if (!subjectSummaries.length) return;

        setIsGeneratingRemarks(true);
        try {
          const gradesForAI = subjectSummaries.map(s => ({
            subjectName: s.subjectName,
            grade: s.grade,
            marks: s.totalScore,
            maxMarks: 100
          }));

          const remarksMap = await generateAutoRemarks(student.name, gradesForAI, schoolID);
          
          setRemarksState(prev => {
              const newState = { ...prev };
              Object.keys(remarksMap).forEach(subjectName => {
                  const summary = subjectSummaries.find(s => s.subjectName === subjectName);
                  if (summary) {
                      newState[summary.subjectId] = remarksMap[subjectName];
                  }
              });
              return newState;
          });

          toast({
              title: "AI Remarks Generated",
              description: "Remarks updated from AI. Disable toggle to edit manually.",
          });

        } catch (error) {
          console.error("Error generating auto remarks:", error);
          toast({
            title: "Generation Failed",
            variant: "destructive"
          });
          // Revert toggle if failed
          setIsAutoRemarksEnabled(false);
        } finally {
          setIsGeneratingRemarks(false);
        }
    }
};

// Open Edit Dialog
  const handleEditClick = (detail: GradeDetail) => {
    setEditingGrade(detail);
    setEditMarks(detail.marks.toString());

    setValidationError(null);
    setIsDialogOpen(true);
  };

  const handleMarksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setEditMarks(newVal);
    
    if (newVal === '') {
      setValidationError(null);
      return;
    }

    const numVal = parseFloat(newVal);
    if (isNaN(numVal)) {
      setValidationError("Please enter a valid number");
    } else if (numVal < 0) {
      setValidationError("Marks cannot be negative");
    } else if (editingGrade && numVal > editingGrade.maxMarks) {
      setValidationError(`Marks cannot exceed ${editingGrade.maxMarks}`);
    } else {
      setValidationError(null);
    }
  };

  const handleSaveGrade = async () => {
    if (!editingGrade || !schoolID) return;
    
    // Final Validation
    const newMarks = parseFloat(editMarks);
    if (isNaN(newMarks) || newMarks < 0 || newMarks > editingGrade.maxMarks) {
      setValidationError(`Invalid marks. Must be 0-${editingGrade.maxMarks}`);
      return;
    }

    setIsSaving(true);
    try {
      // 1. Calculate Grade
      let boundariesForCalc: GradeBoundary[] = [];
      const schemesCollection = collection(db, `schools/${schoolID}/gradingSchemes`);
      const schemesSnapshot = await getDocs(schemesCollection);
      const activeScheme = schemesSnapshot.docs.find(doc => doc.data().isActive);
      if (activeScheme) {
        boundariesForCalc = activeScheme.data().boundaries || [];
      }

      const newGrade = calculateGrade(newMarks, boundariesForCalc);

      // 2. Calculate Normalized Score
      const examType = examTypes.find(et => et.id === editingGrade.examTypeId);
      const examTypeWeight = examType?.weight || 0;
      
      let effectiveWeight = 0;
      if (editingGrade.categoryWeight !== undefined && editingGrade.categoryWeight > 0) {
         effectiveWeight = (editingGrade.categoryWeight / 100) * examTypeWeight;
      } else {
         effectiveWeight = examTypeWeight; // Fallback
      }
      
      const normalizedScore = Number(((newMarks / editingGrade.maxMarks) * effectiveWeight).toFixed(2));

      // 3. Update Firestore
      const gradeRef = doc(db, `schools/${schoolID}/grades`, editingGrade.id);
      await updateDoc(gradeRef, {
        marks: newMarks,
        grade: newGrade,

        normalizedExamScore: normalizedScore,
        updatedAt: serverTimestamp(),
        submittedAt: serverTimestamp(),
        isDraft: false
      });

      toast({
        title: "Grade Updated",
        description: "Marks have been updated successfully.",
      });

      setIsDialogOpen(false);
      fetchStudentDetails(); 

    } catch (error) {
      console.error("Error updating grade:", error);
      toast({
        title: "Update Failed",
        description: "Could not update marks.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
        <p className="text-gray-500 dark:text-gray-400 font-medium animate-pulse">
            Loading student details...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 flex-1 w-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 shadow-sm flex-shrink-0">
                  {student.photoUrl ? (
                      <img src={student.photoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                      <span className="text-lg font-bold text-slate-500">
                          {student.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </span>
                  )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{student.name}</h2>
                <p className="text-gray-500 dark:text-gray-400">
                  {student.regNumber} • {session.name}
                </p>
              </div>
            </div>
          </div>
        </div>
            <div className="flex gap-2 items-center">
            {showAiActions && allowEditing && (
            <div className="flex items-center gap-2 mr-2 bg-purple-50 dark:bg-purple-900/20 px-3 py-2 rounded-lg border border-purple-100 dark:border-purple-800 animate-in fade-in slide-in-from-right-5 duration-300">
                <Switch
                    id="ai-mode"
                    checked={isAutoRemarksEnabled}
                    onCheckedChange={handleAutoRemarksToggle}
                    disabled={isGeneratingRemarks}
                />
                <Label htmlFor="ai-mode" className="text-sm font-medium text-purple-900 dark:text-purple-300 flex items-center gap-2 cursor-pointer">
                    {isGeneratingRemarks ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3 text-purple-500" />}
                    AI Auto Remarks
                </Label>
            </div>
            )}
            


            <Button
                variant="outline"
                onClick={() => setShowReportDialog(true)}
                className="border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
                <FileText className="mr-2 h-4 w-4" /> View Report Form
            </Button>

            {allowEditing && (
            <Button 
                variant={isEditingMode ? "secondary" : "outline"}
                onClick={handleValidation}
                disabled={isSaving}
                className={`transition-all duration-200 ${
                    isEditingMode 
                    ? "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 border border-transparent" 
                    : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
            >
                {isSaving ? (
                     <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                    isEditingMode ? <Check className="mr-2 h-4 w-4" /> : <Edit className="mr-2 h-4 w-4" />
                )}
                {isSaving ? 'Saving...' : (isEditingMode ? 'Done Editing' : 'Edit Results')}
            </Button>
            )}
            <Button variant="outline" onClick={() => window.print()} className="border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
        </div>
        </div>


      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Subject Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-[#1E293B]">
                    <TableHead className="font-semibold text-gray-900 dark:text-white min-w-[150px]">Subject</TableHead>
                    {examTypes.map(et => (
                      <TableHead key={et.id} className="text-center font-semibold text-gray-900 dark:text-white min-w-[120px]">
                        {et.name} ({et.weight}%)
                      </TableHead>
                    ))}
                    <TableHead className="text-center font-semibold text-gray-900 dark:text-white">Total Score</TableHead>
                    <TableHead className="text-center font-semibold text-gray-900 dark:text-white">Grade</TableHead>
                    <TableHead className="text-left font-semibold text-gray-900 dark:text-white">Remarks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subjectSummaries.map((subject) => (
                    <TableRow key={subject.subjectId} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b border-gray-100 dark:border-[#1E293B]">
                      <TableCell className="font-medium text-gray-900 dark:text-white">
                        {subject.subjectName}
                      </TableCell>
                      
                      {examTypes.map(et => {
                        const typeDetails = subject.details.filter(d => d.examTypeId === et.id);
                        const totalTypeScore = typeDetails.reduce((sum, d) => sum + d.normalizedScore, 0);
                        
                        return (
                          <TableCell key={et.id} className="align-top">
                            {typeDetails.length > 0 ? (
                              <div className="flex flex-col gap-1">
                                <div className="text-center font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-1 mb-1">
                                  {totalTypeScore.toFixed(1)}
                                </div>
                                <div className="space-y-1.5">
                                  {typeDetails.map((detail, idx) => (
                                    <div key={idx} className="flex flex-col gap-0.5 text-xs">
                                      <div className="flex justify-between items-center">
                                        <span className="text-gray-700 dark:text-gray-300 font-medium">
                                          {detail.categoryName}
                                        </span>
                                        <span className="text-blue-600 dark:text-blue-400 font-semibold">{detail.normalizedScore.toFixed(1)}</span>
                                      </div>
                                      <div className="flex justify-between items-center text-[10px] mt-1 relative">
                                        <div 
                                            className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded transition-colors ${
                                                isEditingMode 
                                                ? "bg-gray-100 dark:bg-gray-700 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 border border-blue-200 dark:border-blue-800" 
                                                : ""
                                            }`}
                                            onClick={() => isEditingMode && handleEditClick(detail)}
                                        >
                                            <span className="text-green-600 dark:text-green-400">
                                              Marks: {detail.marks}/{detail.maxMarks}
                                            </span>
                                            {isEditingMode && (
                                                <Pencil className="h-3 w-3 text-gray-500 dark:text-gray-400 ml-1" />
                                            )}
                                        </div>
                                        {detail.categoryWeight !== undefined && (
                                          <span className="text-purple-600 dark:text-purple-400">
                                            Weight: {detail.categoryWeight}%
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="text-center text-gray-400">-</div>
                            )}
                          </TableCell>
                        );
                      })}

                      <TableCell className="text-center font-bold text-gray-900 dark:text-white align-top pt-4">
                        {subject.totalScore.toFixed(1)}
                      </TableCell>
                      <TableCell className="text-center align-top pt-4">
                        <Badge className={`${getGradeColor(subject.grade)} text-white hover:opacity-90`}>
                          {subject.grade}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-left text-sm text-gray-600 dark:text-gray-400 align-top pt-4 min-w-[200px]">
                        {isEditingMode ? (
                            <textarea
                                className={`w-full min-h-[60px] p-2 text-sm border rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isAutoRemarksEnabled ? 'opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-800' : ''}`}
                                value={remarksState[subject.subjectId] || ''}
                                onChange={(e) => setRemarksState(prev => ({ ...prev, [subject.subjectId]: e.target.value }))}
                                placeholder={isAutoRemarksEnabled ? "AI generated content..." : "Enter remarks..."}
                                disabled={isAutoRemarksEnabled}
                            />
                        ) : (
                            <span>{remarksState[subject.subjectId] || subject.details.find(d => d.remarks)?.remarks || '-'}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

       {/* Principal Remarks and Academic Analysis Section */}
       <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <Card className="border-slate-200 dark:border-slate-800 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Principal's Remarks
                </CardTitle>
              </CardHeader>
              <CardContent>
                {canEditPrincipalRemarks && isEditingMode ? (
                  <div className="space-y-2">
                    <Textarea 
                      className="min-h-[100px] resize-none font-serif italic"
                      placeholder="Enter principal's remarks..."
                      value={principalRemarks}
                      onChange={(e) => setPrincipalRemarks(e.target.value)}
                    />
                  </div>
                ) : (
                  <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg min-h-[100px] text-sm text-slate-700 dark:text-slate-300 font-serif italic border border-slate-100 dark:border-slate-800">
                    {principalRemarks || "No remarks loaded."}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200 dark:border-slate-800 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Academic Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                {canEditPrincipalRemarks && isEditingMode ? (
                  <div className="space-y-2">
                    <Textarea 
                      className="min-h-[100px] resize-none"
                      placeholder="Enter academic analysis..."
                      value={academicAnalysis}
                      onChange={(e) => setAcademicAnalysis(e.target.value)}
                    />
                  </div>
                ) : (
                  <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg min-h-[100px] text-sm text-slate-700 dark:text-slate-300 border border-slate-100 dark:border-slate-800">
                    {academicAnalysis || "No analysis available."}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Action Footer Removed - Merged into Main "Done Editing" Button */}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400">Total Subjects</span>
                <span className="font-bold text-gray-900 dark:text-white">{subjectSummaries.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400">Average Score</span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {(subjectSummaries.reduce((acc, curr) => acc + curr.totalScore, 0) / (subjectSummaries.length || 1)).toFixed(1)}%
                </span>
              </div>
            </CardContent>
          </Card>
        </div>


      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px] border-gray-200 dark:border-gray-800 dark:bg-gray-950">
          <DialogHeader>
            <DialogTitle className="flex flex-col gap-1">
              <span>Edit Grade Details</span>
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                {student.name}
              </span>
            </DialogTitle>
            <DialogDescription>
              Adjusting marks for <strong>{editingGrade?.subjectName}</strong> - {editingGrade?.examTypeName} ({editingGrade?.categoryName})
              <br/>
              Max allowed marks: <strong>{editingGrade?.maxMarks}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="marks" className="text-gray-700 dark:text-gray-300">Marks Obtained</Label>
              <Input
                id="marks"
                type="number"
                value={editMarks}
                onChange={handleMarksChange}
                className={`col-span-3 ${validationError ? "border-red-500 focus-visible:ring-red-500" : "border-gray-200 dark:border-gray-800 focus-visible:ring-blue-500"}`}
              />
              {validationError && (
                <p className="text-sm text-red-500 font-medium">{validationError}</p>
              )}
            </div>
          </div>
          <DialogFooter className="flex sm:justify-end gap-3">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-gray-200 dark:border-gray-800 dark:text-gray-300 hover:dark:bg-gray-800">Cancel</Button>
            <Button 
              onClick={handleSaveGrade} 
              disabled={isSaving || !!validationError}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" /> Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Form Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full overflow-auto p-0">
          <DialogHeader className="p-4 border-b bg-white dark:bg-slate-950 sticky top-0 z-10">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Student Report Form - {student.name}
            </DialogTitle>
            <DialogDescription>
              Academic report for {session.name}
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 flex justify-center bg-slate-100 dark:bg-slate-900 min-h-[70vh]">
            <ReportPreview
              data={{
                name: student.name,
                studentId: student.regNumber,
                classLevel: studentClass, // Student's class/form
                academicYear: session.name.replace(/term\s*\d+/i, '').trim(), // Year without term
                term: session.name.match(/term\s*(\d+)/i)?.[0] || '', // Extract term from session name
                schoolName: schoolName,
                attendancePercentage: 0, // Could be fetched if available
                classTeacher: "",
                principalRemark: "",
                examTypes: examTypes.map(et => ({ id: et.id, name: et.name, weight: et.weight })),
                position: position, // Legacy fallback
                totalStudents: totalStudents, // Legacy fallback
                classPosition: position, 
                classTotal: totalStudents,
                overallPosition: overallPosition,
                overallTotal: overallTotal,
                subjects: subjectSummaries.map((summary, index) => {
                  // Build dynamic scores object from details
                  const scores: { [examTypeName: string]: number } = {};
                  
                  // Group details by exam type and sum the NORMALIZED scores (weighted values)
                  summary.details.forEach(detail => {
                    const examTypeName = detail.examTypeName || 'Unknown';
                    if (!scores[examTypeName]) {
                      scores[examTypeName] = 0;
                    }
                    // Use normalizedScore for weighted value (e.g., if exam is 20%, score will be out of 20)
                    scores[examTypeName] += Math.round(detail.normalizedScore * 10) / 10; // Round to 1 decimal
                  });
                  
                  // Get remarks from the last detail entry
                  const lastDetail = summary.details[summary.details.length - 1];
                  
                  return {
                    id: summary.subjectId || `${index}`,
                    name: summary.subjectName,
                    scores: scores, // Dynamic exam type scores
                    score: Math.round(summary.totalScore),
                    total: 100,
                    teacherRemarks: lastDetail?.remarks || ""
                  };
                })
              } as StudentData}
              template="default"
            />
          </div>
          <DialogFooter className="p-4 border-t bg-white dark:bg-slate-950">
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Close
            </Button>
            <Button onClick={() => window.print()}>
              <Printer className="mr-2 h-4 w-4" /> Print Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentResultDetails;
